import * as React from 'react';
import { UpdateIcon, QuestionMarkCircledIcon } from '@radix-ui/react-icons';

type LoadState = 'pending' | 'loading' | 'loaded' | 'error';

type FallbackImgProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'src' | 'loading'
> & {
  src?: string | null;
  lazyLoad?: boolean; // Enable intersection observer lazy loading
  // Legacy props for backward compatibility (ignored)
  fallback?: string;
  fallbackWidth?: number;
  fallbackHeight?: number;
};

export function FallbackImg({
  src,
  alt = '',
  className = '',
  lazyLoad = true,
  // Destructure legacy props to prevent them from being spread to DOM
  fallback,
  fallbackWidth,
  fallbackHeight,
  style,
  ...rest
}: FallbackImgProps) {
  const [loadState, setLoadState] = React.useState<LoadState>(
    lazyLoad ? 'pending' : 'loading',
  );
  const [shouldLoad, setShouldLoad] = React.useState(!lazyLoad); // Load immediately if lazy disabled
  const [retryAttempted, setRetryAttempted] = React.useState(false); // Track if we've already retried
  const imgRef = React.useRef<HTMLImageElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const loadStartTime = React.useRef<number>(0);
  const retryTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const hasLoadedRef = React.useRef<boolean>(false);

  // Reset retry state when src changes
  React.useEffect(() => {
    setRetryAttempted(false);
    setLoadState(lazyLoad ? 'pending' : 'loading');
    hasLoadedRef.current = false;
  }, [src, lazyLoad]);

  // Intersection Observer for lazy loading (2 viewports ahead)
  React.useEffect(() => {
    if (!lazyLoad || shouldLoad) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '200% 0px', // Start loading 2 viewports ahead
      },
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [lazyLoad, shouldLoad]);

  // Handle image load events
  React.useEffect(() => {
    if (!shouldLoad) {
      // Waiting for intersection observer
      return;
    }

    if (!src) {
      // No src provided - stay in pending state (don't show error)
      return;
    }

    const img = imgRef.current;
    if (!img) return;

    // Check if image is already loaded (from cache or previous load)
    // Use setTimeout to allow browser to check cache first (microtask timing)
    const checkCached = () => {
      if (img.complete && img.naturalHeight !== 0) {
        setLoadState('loaded');
        hasLoadedRef.current = true;
        // Cancel retry timer if image loaded successfully
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
        return true;
      }
      return false;
    };

    // Immediate check
    if (checkCached()) {
      return;
    }

    setLoadState('loading');
    loadStartTime.current = Date.now();

    // Also check again after microtask (for newly mounted components with cached images)
    const timeoutId = setTimeout(() => {
      if (checkCached()) {
        // Image was cached, cancel the loading state
        return;
      }
    }, 0);

    // Set up 5-second retry timer (only on first attempt)
    if (!retryAttempted) {
      const timerId = setTimeout(() => {
        if (!hasLoadedRef.current && img) {
          setRetryAttempted(true);
          // Force reload by updating the img src
          const currentSrc = img.src;
          img.src = '';
          // Use requestAnimationFrame to ensure browser processes the empty src
          requestAnimationFrame(() => {
            img.src = currentSrc;
          });
        }
      }, 5000);
      retryTimerRef.current = timerId;
    }

    const handleLoad = () => {
      hasLoadedRef.current = true;
      setLoadState('loaded');
      // Cancel retry timer on successful load
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const handleError = (e: ErrorEvent | Event) => {
      // Cancel retry timer on error
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      // Only show error state if we've already retried
      if (retryAttempted) {
        setLoadState('error');
      }
      // First failure - retry will happen via timer (silent)
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    return () => {
      clearTimeout(timeoutId);
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  }, [src, shouldLoad]);

  return (
    <div ref={containerRef} className={className}>
      {/* Pending state - waiting for lazy load (show nothing) */}
      {loadState === 'pending' && (
        <div className="absolute inset-0 bg-transparent" />
      )}

      {/* Loading spinner */}
      {loadState === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-transparent">
          <UpdateIcon className="w-5 h-5 text-white animate-[spin_3s_linear_infinite]" />
        </div>
      )}

      {/* Error state - Not Found icon */}
      {loadState === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-transparent">
          <QuestionMarkCircledIcon className="w-12 h-12 text-gray-300" />
        </div>
      )}

      {/* Actual image - hidden until loaded */}
      {shouldLoad && src && (
        <img
          {...rest}
          ref={imgRef}
          src={src}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-200"
          style={{
            ...style,
            opacity: loadState === 'loaded' ? 1 : 0,
          }}
        />
      )}
    </div>
  );
}
